import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity, Alert} from 'react-native';
import {Text, ActivityIndicator} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import api from '../../services/api/axiosInstance';
import {colors, spacing} from '../../theme';

const STAT_META = {
  ordersToday: {icon: 'cart-outline', accent: '#2196F3'},
  pendingOrders: {icon: 'clock-outline', accent: '#FF9800'},
  readyForPickup: {icon: 'check-circle-outline', accent: '#4CAF50'},
  overdueOrders: {icon: 'alert-circle-outline', accent: '#F44336'},
};

const StatItem = ({value, label, icon, accent}) => (
  <View style={styles.statItem}>
    <View style={[styles.statIconCircle, {backgroundColor: accent + '15'}]}>
      <Icon name={icon} size={20} color={accent} />
    </View>
    <View style={styles.statText}>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel} numberOfLines={2}>{label}</Text>
    </View>
  </View>
);

const DashboardScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/summary');
      setSummary(response.data.data);
    } catch (e) {
      // silently fail on dashboard
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchSummary().finally(() => setLoading(false));
  }, [fetchSummary]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchSummary();
    setRefreshing(false);
  }, [fetchSummary]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('dashboard.title')}
        onMenu={() => navigation.dispatch(DrawerActions.openDrawer())}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>

        {/* Order stats */}
        <View style={styles.statsGrid}>
          <StatItem value={summary?.ordersToday ?? 0} label={t('dashboard.ordersToday')} {...STAT_META.ordersToday} />
          <StatItem value={summary?.pendingOrders ?? 0} label={t('dashboard.pendingOrders')} {...STAT_META.pendingOrders} />
          <StatItem value={summary?.readyForPickup ?? 0} label={t('dashboard.readyForPickup')} {...STAT_META.readyForPickup} />
          <StatItem value={summary?.overdueOrders ?? 0} label={t('dashboard.overdueOrders')} {...STAT_META.overdueOrders} />
        </View>

        {/* Revenue */}
        {summary?.totalRevenue != null && (
          <View style={styles.revenueGrid}>
            {/* Delivered Revenue */}
            <View style={styles.revenueTile}>
              <View style={styles.tileLabelRow}>
                <Icon name="trending-up" size={16} color="#1565C0" />
                <Text style={styles.revenueLabel}>{t('dashboard.deliveredRevenue')}</Text>
                <TouchableOpacity
                  onPress={() => Alert.alert(
                    t('dashboard.deliveredRevenue'),
                    t('dashboard.deliveredRevenueHelp'),
                  )}
                  hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
                  <Icon name="information-outline" size={15} color="#90CAF9" />
                </TouchableOpacity>
              </View>
              <Text style={styles.revenueAmount}>
                {'\u20B9'}{Number(summary.totalRevenue).toLocaleString()}
              </Text>
            </View>

            {/* Delivered Pending */}
            <View style={styles.deliveredPendingTile}>
              <View style={styles.tileLabelRow}>
                <Icon name="cash-clock" size={16} color="#AD1457" />
                <Text style={styles.deliveredPendingLabel}>{t('dashboard.deliveredPending')}</Text>
                <TouchableOpacity
                  onPress={() => Alert.alert(
                    t('dashboard.deliveredPending'),
                    t('dashboard.deliveredPendingHelp'),
                  )}
                  hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
                  <Icon name="information-outline" size={15} color="#F48FB1" />
                </TouchableOpacity>
              </View>
              <Text style={styles.deliveredPendingAmount}>
                {'\u20B9'}{Number(summary.deliveredPending || 0).toLocaleString()}
              </Text>
            </View>

            {/* Overall Pending */}
            {summary.pendingPayments > 0 && (
              <View style={styles.pendingTile}>
                <View style={styles.tileLabelRow}>
                  <Icon name="clock-alert-outline" size={16} color="#E65100" />
                  <Text style={styles.pendingLabel}>{t('dashboard.overallPending')}</Text>
                  <TouchableOpacity
                    onPress={() => Alert.alert(
                      t('dashboard.overallPending'),
                      t('dashboard.overallPendingHelp'),
                    )}
                    hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
                    <Icon name="information-outline" size={15} color="#FFCC80" />
                  </TouchableOpacity>
                </View>
                <Text style={styles.pendingAmount}>
                  {'\u20B9'}{Number(summary.pendingPayments).toLocaleString()}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Quick actions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('dashboard.quickActions')}</Text>
          <View style={styles.actionRow}>
            <TouchableOpacity
              style={[styles.actionTile, styles.actionTilePrimary]}
              activeOpacity={0.7}
              onPress={() => navigation.navigate('Orders', {screen: 'OrdersTab', params: {screen: 'OrderCreate'}})}>
              <Icon name="plus" size={20} color={colors.white} />
              <Text style={[styles.actionTileText, {color: colors.white}]}>{t('dashboard.newOrder')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionTile}
              activeOpacity={0.7}
              onPress={() => navigation.navigate('Customers', {screen: 'CustomerForm'})}>
              <Icon name="account-plus" size={20} color={colors.primary} />
              <Text style={styles.actionTileText}>{t('dashboard.newCustomer')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionTile}
              activeOpacity={0.7}
              onPress={() => navigation.navigate('Expenses', {screen: 'ExpenseForm'})}>
              <Icon name="cash-plus" size={20} color={colors.primary} />
              <Text style={styles.actionTileText}>{t('dashboard.addExpense')}</Text>
            </TouchableOpacity>
          </View>
        </View>

      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollView: {flex: 1},
  scrollContent: {padding: spacing.md, gap: spacing.md},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  // Sections
  section: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },

  // Stats grid - 2x2
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  statItem: {
    width: '47%',
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 12,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  statIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statText: {
    flex: 1,
  },
  statValue: {
    fontSize: 25,
    fontWeight: '800',
    color: colors.text,
    lineHeight: 28,
  },
  statLabel: {
    fontSize: 11,
    color: colors.textSecondary,
    fontWeight: '500',
    lineHeight: 14,
  },

  // Revenue grid
  revenueGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  tileLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  revenueTile: {
    width: '47%',
    flexGrow: 1,
    backgroundColor: '#E3F2FD',
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#BBDEFB',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  revenueLabel: {
    fontSize: 11,
    color: '#1565C0',
    fontWeight: '600',
    flex: 1,
  },
  revenueAmount: {
    fontSize: 20,
    fontWeight: '800',
    color: '#0D47A1',
  },
  deliveredPendingTile: {
    width: '47%',
    flexGrow: 1,
    backgroundColor: '#FCE4EC',
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#F8BBD0',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  deliveredPendingLabel: {
    fontSize: 11,
    color: '#AD1457',
    fontWeight: '600',
    flex: 1,
  },
  deliveredPendingAmount: {
    fontSize: 20,
    fontWeight: '800',
    color: '#880E4F',
  },
  pendingTile: {
    width: '100%',
    backgroundColor: '#FFF3E0',
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#FFE0B2',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  pendingLabel: {
    fontSize: 11,
    color: '#E65100',
    fontWeight: '600',
    flex: 1,
  },
  pendingAmount: {
    fontSize: 20,
    fontWeight: '800',
    color: '#BF360C',
  },

  // Quick actions
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  actionRow: {flexDirection: 'row', gap: spacing.sm},
  actionTile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: colors.background,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: spacing.xs,
  },
  actionTilePrimary: {
    backgroundColor: colors.primary,
  },
  actionTileText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
    flexShrink: 1,
  },
});

export default DashboardScreen;
